import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { ConfigService } from './config.service';
import { CountrySetting } from '../models/countrysetting';
import { timeout, retry, map, shareReplay } from 'rxjs/operators';
import {
    GetClient,
    Site,
    GetCommonStudyDescription,
    PaginatedResponseOfGetCommonStudyDescription
} from '.././api-module/control-centre-api-client';

const HTTP_TIMEOUT_MSECONDS = 20000;
const HTTP_RETRY = 3;

const countrySettings: CountrySetting[] = [
    { country: 'AU', region: 'ANZ', countryCode: '+61' },
    { country: 'UK', region: 'UKI', countryCode: '+44' },
    { country: 'TEST', region: 'UKI', countryCode: '+44' },
    { country: 'NZ', region: 'ANZ', countryCode: '+64' },
    { country: 'IE', region: 'UKI', countryCode: '+353' },
    { country: 'QA', region: 'ANZ', countryCode: '+974' },
    { country: 'ZA', region: 'ANZ', countryCode: '+27' }
];

@Injectable({
    providedIn: 'root'
})
export class SettingsService {
    constructor(private readonly http: HttpClient) {}

    static groupBy<T, K>(sourceArray: T[], getKey: (item: T) => K): Map<K, T[]> {
        if (!sourceArray) {
            return null;
        }

        const m = new Map<K, T[]>();

        sourceArray.forEach(item => {
            const key = getKey(item);
            const collection = m.get(key);

            if (!collection) {
                m.set(key, [item]);
            } else {
                collection.push(item);
            }
        });
        return m;
    }

    /**
     * previously apiServer as a property will cause runtime error (apiServer undefined)
     * if SettingsService is injected into AuthenticationService
     * @returns API url for CC.api as string
     */
    getAPIServer(): string {
        return ConfigService.settings?.apiServer?.controlCenter;
    }

    getClientByOrg(orgCode: string, includeSettings: boolean = false): Observable<GetClient> {
        return this.http
            .get<GetClient>(this.getAPIServer() + `v1/Settings/Clients/` + orgCode + `?includeSettings=` + includeSettings)
            .pipe(timeout(HTTP_TIMEOUT_MSECONDS), retry(HTTP_RETRY), shareReplay());
    }

    getClients(): Observable<GetClient[]> {
        return this.http
            .get<GetClient[]>(this.getAPIServer() + `v1/Settings/Clients`)
            .pipe(timeout(HTTP_TIMEOUT_MSECONDS), retry(HTTP_RETRY));
    }

    getUrgencies(): Observable<any[]> {
        return this.http.get<any[]>(this.getAPIServer() + `v1/Settings/Urgencies`).pipe(timeout(HTTP_TIMEOUT_MSECONDS), retry(HTTP_RETRY));
    }

    getSitesByOrg(orgCode: string): Observable<Site[]> {
        return this.http
            .get<Site[]>(this.getAPIServer() + `v1/Settings/Sites/` + orgCode)
            .pipe(timeout(HTTP_TIMEOUT_MSECONDS), retry(HTTP_RETRY), shareReplay());
    }

    getCommonStudies(): Observable<GetCommonStudyDescription[]> {
        return this.http.get<PaginatedResponseOfGetCommonStudyDescription>(this.getAPIServer() + `v1/common-study-descriptions`).pipe(
            map(paginatedResponse => paginatedResponse.items),
            timeout(HTTP_TIMEOUT_MSECONDS),
            retry(HTTP_RETRY)
        );
    }

    getCountrySettings(): Observable<CountrySetting[]> {
        if (countrySettings) {
            return of(countrySettings);
        } else {
            return this.http
                .get<CountrySetting[]>(this.getAPIServer() + `v1/Settings/CountrySettings`)
                .pipe(timeout(HTTP_TIMEOUT_MSECONDS), retry(HTTP_RETRY));
        }
    }

    getModalities(): Observable<string[]> {
        return this.getCommonStudies().pipe(
            map(commonStudies => {
                // Group common studies based on modality
                const studiesGroups = SettingsService.groupBy(commonStudies, commonStudy => commonStudy.modality);

                if (studiesGroups) {
                    studiesGroups.forEach(value => {
                        value.sort((a, b) => a.description.localeCompare(b.description));
                    });

                    return [...studiesGroups.keys()].sort((a, b) => a.localeCompare(b));
                }
            })
        );
    }
}
